import * as Sentry from '@sentry/react';

/**
 * Ensure Sentry is recording a *session* replay rather than an error buffer.
 *
 * ``initSentry`` sets ``replaysOnErrorSampleRate: 1``, which puts every user in
 * buffer mode from boot: the last ~60s is held in memory and uploaded only when
 * an error is sampled. Two consequences, both of which broke the previous
 * ``if (!replay.getReplayId()) { replay.start(); }`` version of this code:
 *
 * - a buffered session already has an id, so ``getReplayId()`` is truthy and the
 *   guard never allowed ``start()`` to run at all;
 * - ``start()`` is a documented no-op while a recording is already in progress,
 *   so it would not have converted the buffer even if it had been reached.
 *
 * ``flush()`` is the SDK's supported conversion: in buffer mode it uploads the
 * pending segment and carries on recording as a session, and when replay is
 * stopped entirely it starts a fresh session recording. That covers every state
 * except "already recording a session", which is handled below.
 *
 * @see https://docs.sentry.io/platforms/javascript/session-replay/understanding-sessions/
 */
export function ensureSentryReplayRecording(): void {
    const replay = Sentry.getReplay();
    if (!replay) { return; }

    // Already recording a session. Flushing again would only force a redundant
    // segment upload, and this runs on every route change.
    if (replay.getRecordingMode() === 'session') { return; }

    // Fire-and-forget: the SDK reports its own flush failures via
    // `handleException`, so there is nothing useful to await or catch here.
    void replay.flush();
}

/**
 * Whether this page load is sampled for interaction-triggered recording.
 *
 * Deliberately module-level: the decision has to be made once and then reused,
 * because the callers fire on every calendar click. Rolling per call compounds —
 * a lost roll leaves the session buffered, so each later click is another chance
 * to convert the same session, and at a configured 0.3 five interactions reach
 * ~83% (``1 - 0.7^5``) rather than 30%.
 */
let isSampledForSession: boolean | null = null;

/**
 * Start a Sentry session replay on demand, gated by a client-side sample rate.
 *
 * Replay is otherwise buffer-only (``replaysSessionSampleRate`` is 0 in
 * ``initSentry``), so this lets specific interactions opt a fraction of sessions
 * into full recording. A no-op if this session lost the roll, if replay is
 * unavailable (e.g. development, where Sentry is disabled), or if a session
 * recording is already in progress.
 */
export function startSentryReplaySampled(): void {
    if (isSampledForSession === null) {
        const sampleRate = Number(import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE ?? 0);
        isSampledForSession = Math.random() < sampleRate;
    }

    if (!isSampledForSession) {
        return;
    }

    ensureSentryReplayRecording();
}
